/**
 * The single definition of "this location belongs to the workspace that is
 * currently selected, and this user may use it".
 *
 * Production defect this exists for: a user who is a member of three
 * workspaces had SUPERBOL selected while the location switcher's collapsed
 * label read "Laval" — a location of 9849769 Canada Inc. The dropdown listed
 * only SUPERBOL's Saint-Jerome, because the *list* is derived per workspace,
 * so the selected item was not even among the options. The database was
 * consistent throughout; only the client's `location` state was foreign, and
 * it got there because an auto-selection fetch answered for the previous
 * workspace. Patching that one caller would leave every other present and
 * future setter (`setLocation` is exported on the context) free to do it
 * again, so the rule lives here and WorkspaceContext enforces it in exactly
 * one place.
 *
 * `accessibleLocationIds` is the id set of the current workspace's locations
 * as the server reported them for THIS user (`/workspaces/me/accessible`
 * already returns every location of the workspace for workspace admins,
 * and only the assigned ones otherwise) — so membership in that set is both
 * the workspace-scope test and the permission test, and there is no second
 * access check to re-roll.
 */
export interface LocationScope {
  /**
   * `null` means the current workspace's location list has NOT been resolved
   * yet; `[]` means it resolved and the workspace exposes none.
   *
   * The distinction is carried by the type rather than by a companion boolean
   * because the two states are otherwise the same value, and reading the
   * first as the second clears a perfectly valid selection while the app is
   * still loading. A caller cannot spell "I don't know yet" as `[]` here.
   */
  accessibleLocationIds: readonly string[] | null;
}

/** Is `locationId` one of the current workspace's locations for this user? */
export function isLocationInScope(scope: LocationScope, locationId: string): boolean {
  return scope.accessibleLocationIds?.includes(locationId) ?? false;
}

/**
 * Must the held location be dropped to null ("All Locations", a state the
 * switcher already renders)?
 *
 * Answers `false` while the scope is unresolved. That branch is not a
 * theoretical nicety: `CreateWorkspaceModal` switches to a just-created
 * workspace with `skipAccessCheck` and then calls
 * `refreshAccessibleWorkspaces()` WITHOUT awaiting it, so there is a real
 * window in which `workspace` is the new one and the accessible list still
 * describes the old one. Clearing on emptiness alone would also wipe the
 * selection on every cold load between the workspace landing in state and
 * the membership list arriving — a visible flip of the switcher label to
 * "All Locations" and back.
 */
export function shouldClearLocation(
  scope: LocationScope,
  locationId: string | null | undefined,
): boolean {
  if (!locationId) return false;
  if (scope.accessibleLocationIds === null) return false;
  return !isLocationInScope(scope, locationId);
}
